import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';
import { browserHistory } from 'react-router';

import { IconButton, Menu, MenuItem } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { graphql } from 'babel-plugin-relay/macro';
import { compact, get, map } from 'lodash';

import { ProvisioningEnum } from '@lib/resources/types';

import { DialogChatActivityClose, IconButtonProps } from 'components/v2';
import usePermissions from 'hooks/usePermissions';
import { isAdmin } from 'utils/permissions';

import { IconButtonPatientActions_Patient$key } from './__generated__/IconButtonPatientActions_Patient.graphql';
import { IconButtonPatientActions_Viewer$key } from './__generated__/IconButtonPatientActions_Viewer.graphql';

const messages = defineMessages({
  menuOptionClose: {
    id: 'deprecated.fragments.IconButtonPatientActions.menuOptionClose',
    defaultMessage: 'Mark as closed',
  },
  menuOptionEdit: {
    id: 'deprecated.fragments.IconButtonPatientActions.menuOptionEdit',
    defaultMessage: 'Edit patient information',
  },
  menuOptionMerge: {
    id: 'deprecated.fragments.IconButtonPatientActions.menuOptionMerge',
    defaultMessage: 'Merge patient',
  },
  menuOptionPatient: {
    id: 'deprecated.fragments.IconButtonPatientActions.menuOptionPatient',
    defaultMessage: 'Go to patient profile',
  },
  menuOptionSchedule: {
    id: 'deprecated.fragments.IconButtonPatientActions.menuOptionSchedule',
    defaultMessage: 'Schedule appointment',
  },
});

type ActionKey = 'closeChatActivity' | 'editPatient' | 'goToPatient' | 'mergePatient' | 'scheduleAppointment';

export interface IconButtonPatientActionsProps extends Omit<IconButtonProps, 'classes'> {
  fragmentRefViewer: IconButtonPatientActions_Viewer$key;
  fragmentRef: IconButtonPatientActions_Patient$key;
  actions: ActionKey[];
  onAction: (key: string, patient: any) => void;
}

const IconButtonPatientActions: React.FC<IconButtonPatientActionsProps> = ({
  fragmentRefViewer,
  fragmentRef,
  actions,
  onAction,
}) => {
  const viewer = useFragment<IconButtonPatientActions_Viewer$key>(
    graphql`
      fragment IconButtonPatientActions_Viewer on User {
        _id
        roles
      }
    `,
    fragmentRefViewer,
  );

  const patient = useFragment<IconButtonPatientActions_Patient$key>(
    graphql`
      fragment IconButtonPatientActions_Patient on Patient
        @argumentDefinitions(includeChatActivity: { type: "Boolean", defaultValue: true }) {
        _id
        chatActivity @include(if: $includeChatActivity) {
          _id
          status
          assignee {
            _id
          }
          groupAssignee {
            _id
          }
        }
      }
    `,
    fragmentRef,
  );

  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);
  const { _id, chatActivity } = patient;

  const { hasAccessToProduct } = usePermissions();
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<any>();

  const menuItems = useMemo(
    () =>
      compact(
        map(actions, (actionKey) => {
          let element: JSX.Element | undefined;
          switch (actionKey) {
            case 'closeChatActivity': {
              if (
                chatActivity &&
                chatActivity.status === 'assigned' &&
                (get(chatActivity.assignee, '_id') === viewer._id ||
                  get(chatActivity.groupAssignee, '_id') === viewer._id)
              ) {
                element = (
                  <MenuItem
                    data-lh-id="patient-actions-mark-as-closed-menu-item"
                    key="closed"
                    onClick={() => {
                      setOpen(true);
                      setMoreMenuAnchor(undefined);
                    }}
                  >
                    {formatMessage(messages.menuOptionClose)}
                  </MenuItem>
                );
              }
              break;
            }
            case 'editPatient':
              element = (
                <MenuItem
                  data-lh-id="patient-actions-edit-patient-information-menu-item"
                  key="edit"
                  onClick={() => {
                    browserHistory.push(`/patients/${_id}/edit`);
                    setMoreMenuAnchor(undefined);
                  }}
                >
                  {formatMessage(messages.menuOptionEdit)}
                </MenuItem>
              );
              break;
            case 'goToPatient':
              element = (
                <MenuItem
                  data-lh-id="patient-actions-go-to-patient-profile-menu-item"
                  key="patient"
                  onClick={() => {
                    window.open(`/patients/${_id}/chat`, '_blank');
                    setMoreMenuAnchor(undefined);
                  }}
                >
                  {formatMessage(messages.menuOptionPatient)}
                </MenuItem>
              );
              break;
            case 'mergePatient': {
              // @ts-ignore
              if (isAdmin(viewer)) {
                element = (
                  <MenuItem
                    key="merge"
                    data-lh-id="patient-actions-merge-patient-menu-item"
                    onClick={() => {
                      onAction('merge', patient);
                      setMoreMenuAnchor(undefined);
                    }}
                  >
                    {formatMessage(messages.menuOptionMerge)}
                  </MenuItem>
                );
              }
              break;
            }
            case 'scheduleAppointment': {
              const hasAccessToSchedulerAssist = hasAccessToProduct(ProvisioningEnum.SchedulerAssist);
              const hasAccessToSchedulerLegacy = hasAccessToProduct(ProvisioningEnum.Scheduler);
              const hasAccessToScheduler = hasAccessToSchedulerAssist || hasAccessToSchedulerLegacy;
              if (hasAccessToScheduler) {
                const route = hasAccessToSchedulerAssist
                  ? `/scheduler-assist?patient=${_id}`
                  : `/patients/${_id}/scheduler`;
                element = (
                  <MenuItem
                    key="schedule"
                    data-lh-id="patient-actions-schedule-appointment-menu-item"
                    onClick={() => {
                      browserHistory.push(route);
                      setMoreMenuAnchor(undefined);
                    }}
                  >
                    {formatMessage(messages.menuOptionSchedule)}
                  </MenuItem>
                );
              }
              break;
            }
            default:
              break;
          }
          return element;
        }),
      ),
    [actions, viewer, patient, chatActivity, hasAccessToProduct, _id, onAction, formatMessage],
  );

  return (
    <>
      <IconButton
        data-lh-id="patient-actions-more-menu-button"
        aria-label="More"
        aria-owns={moreMenuAnchor ? 'more-menu' : undefined}
        aria-haspopup="true"
        onClick={(event) => setMoreMenuAnchor(event.currentTarget)}
      >
        <MoreVertIcon color="primary" />
      </IconButton>
      <Menu
        id="more-menu"
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(undefined)}
      >
        {menuItems}
      </Menu>
      {chatActivity && <DialogChatActivityClose ids={[chatActivity._id]} open={open} onClose={() => setOpen(false)} />}
    </>
  );
};

export default IconButtonPatientActions;
